import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import type { ChangeEvent, CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { twMerge } from 'tailwind-merge';

import { TextStyle } from '../../DesignSystem/Feedback/TextStyle';
import { Button } from '../../DesignSystem/Inputs/Button';
import { Checkbox } from '../../DesignSystem/Inputs/Checkbox';
import { IconButton } from '../../DesignSystem/Inputs/IconButton';
import { TextField } from '../../DesignSystem/Inputs/TextField';

const messages = defineMessages({
  clearAll: {
    defaultMessage: 'Clear all',
    id: 'QW+Q5N',
  },
  done: {
    defaultMessage: 'Done',
    id: 'JXdbo8',
  },
  noResultsFound: {
    defaultMessage: 'No results for this search. Please try again.',
    id: 'AW0ulw',
  },
});

export type SelectableOptionProps = {
  id: string;
  disabled?: boolean;
  [key: string]: string | undefined | boolean | number;
};

export type SelectableListProps = {
  isLoading: boolean;
  className?: string;
  placeholder?: string;
  showSearch?: boolean;
  hasNextPage?: boolean;
  onLoadMore: () => void;
  onBackButtonClick?: () => void;
  showSelectedOnTop?: boolean;
  isFetchingNextPage?: boolean;
  options: SelectableOptionProps[];
  buttonsWrapperClassName?: string;
  nestedDropdownHeaderText?: string;
  showNestedDropdownHeader: boolean;
  onSearchChange: (value: string) => void;
  selectedOptions: SelectableOptionProps[];
  loader: React.ReactNode;
  renderOption: (option: SelectableOptionProps) => React.ReactNode;
  onDoneClick: (currentSelectedOptions: SelectableOptionProps[]) => void;
  onClearAllClick: () => void;
};

function Options({
  index,
  options,
  focusedOptionIndex,
  handleOptionSelect,
  renderOption,
  currentSelectedOptions,
}: {
  index: number;
  focusedOptionIndex: number;
  options: SelectableOptionProps[];
  currentSelectedOptions: SelectableOptionProps[];
  renderOption: (option: SelectableOptionProps) => React.ReactNode;
  handleOptionSelect: (selectedOption: SelectableOptionProps) => void;
}) {
  if (index >= options.length) {
    return null;
  }

  const option = options[index];

  const isItemFocused = index === focusedOptionIndex;

  return (
    <div
      className={twMerge(
        'flex h-[40px] cursor-pointer items-center gap-2 px-4 outline-none hover:bg-gray-2 focus:bg-gray-2',
        isItemFocused && 'bg-gray-2',
        option.disabled && 'cursor-auto'
      )}
      tabIndex={0}
      role="menuitem"
      aria-label={`selectable option - ${option.id}`}
      onKeyDown={(event) => {
        if (event.code === 'Enter' || event.code === 'Space') {
          handleOptionSelect(option);
        }
      }}
      aria-disabled={option.disabled}
      onClick={() => {
        if (!option.disabled) {
          handleOptionSelect(option);
        }
      }}
    >
      <Checkbox
        name={`${option.id}-selectable-option`}
        aria-label={`Checkbox for selectable option - ${option.id}`}
        checked={currentSelectedOptions.some(
          (selectedOption) => selectedOption.id === option.id
        )}
        onChange={() => handleOptionSelect(option)}
        tabIndex={-1}
        disabled={option.disabled}
      />
      {renderOption(option)}
    </div>
  );
}

function ListItem({
  index,
  style,
  data,
}: {
  index: number;
  style: CSSProperties;
  data: {
    loader: ReactNode;
    focusedOptionIndex: number;
    isFetchingNextPage?: boolean;
    options: SelectableOptionProps[];
    currentSelectedOptions: SelectableOptionProps[];
    renderOption: (option: SelectableOptionProps) => ReactNode;
    handleOptionSelect: (selectedOption: SelectableOptionProps) => void;
  };
}) {
  return (
    <div style={style}>
      <Options
        index={index}
        options={data.options}
        renderOption={data.renderOption}
        handleOptionSelect={data.handleOptionSelect}
        focusedOptionIndex={data.focusedOptionIndex}
        currentSelectedOptions={data.currentSelectedOptions}
      />
      {index === data.options.length - 1 &&
        data.isFetchingNextPage &&
        data.loader}
    </div>
  );
}

export function SelectableList({
  options,
  className,
  isLoading,
  showSearch,
  onLoadMore,
  placeholder,
  hasNextPage,
  onDoneClick,
  onClearAllClick,
  onBackButtonClick,
  onSearchChange,
  selectedOptions,
  isFetchingNextPage,
  buttonsWrapperClassName,
  showNestedDropdownHeader,
  nestedDropdownHeaderText,
  loader,
  renderOption,
}: SelectableListProps) {
  const { formatMessage } = useIntl();
  const [searchValue, setSearchValue] = useState('');
  const [currentSelectedOptions, setCurrentSelectedOptions] =
    useState(selectedOptions);

  const [optionsToRender, setOptionsToRender] = useState(options);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);

  const unSelectedOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          !selectedOptions.some(
            (selectedOption) => selectedOption.id === option.id
          )
      ),
    [options, selectedOptions]
  );

  useEffect(() => {
    if (selectedOptions.length) {
      const optionsToRender = [...selectedOptions, ...unSelectedOptions];
      setOptionsToRender(optionsToRender);
    } else {
      setOptionsToRender(options);
    }
  }, [options, selectedOptions, unSelectedOptions]);

  const itemSize = 40;
  const listRef = useRef<FixedSizeList | null>(null);
  const isItemLoaded = (index: number) => index < options.length;
  const itemCount = hasNextPage ? options.length + 1 : options.length;

  const handleOptionSelect = useCallback(
    (selectedOption: SelectableOptionProps) => {
      const index = currentSelectedOptions.findIndex(
        (option) => option.id === selectedOption.id
      );
      if (index !== -1) {
        const modifiedSelectedOptions = [...currentSelectedOptions];
        modifiedSelectedOptions.splice(index, 1);
        setCurrentSelectedOptions(modifiedSelectedOptions);
      } else {
        setCurrentSelectedOptions([...currentSelectedOptions, selectedOption]);
      }
    },
    [currentSelectedOptions]
  );

  const handleClearAllClick = () => {
    setOptionsToRender(options);
    setCurrentSelectedOptions([]);
    onClearAllClick();
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedOptionIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : prevIndex
        );
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedOptionIndex((prevIndex) =>
          prevIndex < optionsToRender.length - 1 ? prevIndex + 1 : prevIndex
        );
      } else if (
        (e.key === 'Enter' || e.code === 'Space') &&
        focusedOptionIndex >= 0
      ) {
        e.preventDefault();
        handleOptionSelect(optionsToRender[focusedOptionIndex]);
      }

      // Scroll to the focused item
      if (listRef.current) {
        listRef.current.scrollToItem(focusedOptionIndex, 'center');
      }
    },
    [focusedOptionIndex, handleOptionSelect, optionsToRender]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [focusedOptionIndex, handleKeyDown, handleOptionSelect, optionsToRender]);

  const listHeight = options.length ? options.length * itemSize : itemSize;

  return (
    <section>
      <div
        className={twMerge(
          'box-border flex h-fit max-h-[240px] w-[280px] flex-col gap-2 rounded-lg rounded-b-none bg-gray-1 py-2',
          ((!isLoading && itemCount === 0) || isLoading) && 'rounded-b-lg',
          className
        )}
      >
        {showNestedDropdownHeader && (
          <div className="flex gap-2 px-2">
            <IconButton
              size="xSmall"
              onClick={onBackButtonClick}
              variation="tertiaryLite"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            </IconButton>
            <TextStyle className="text-gray-8">
              {nestedDropdownHeaderText}
            </TextStyle>
          </div>
        )}
        {showSearch && (
          <div className="px-2">
            <TextField
              type="text"
              value={searchValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setSearchValue(e.target.value);
                onSearchChange(e.target.value);
              }}
              invalidText=""
              isInvalid={false}
              placeholder={placeholder}
              connectedLeft={
                <MagnifyingGlassIcon className="pointer-events-none h-4 w-4 text-gray-9" />
              }
              aria-label={placeholder}
            />
          </div>
        )}
        <div style={{ height: listHeight }} role="menu">
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                itemCount={itemCount}
                loadMoreItems={onLoadMore}
                isItemLoaded={isItemLoaded}
              >
                {({ onItemsRendered, ref }) => (
                  <FixedSizeList
                    width={width}
                    itemData={{
                      options: optionsToRender,
                      loader,
                      renderOption,
                      optionsToRender,
                      isFetchingNextPage,
                      handleOptionSelect,
                      focusedOptionIndex,
                      currentSelectedOptions,
                    }}
                    height={height}
                    itemSize={itemSize}
                    itemCount={itemCount}
                    onItemsRendered={onItemsRendered}
                    ref={(list) => {
                      listRef.current = list;
                      ref(list);
                    }}
                    style={{ overflowX: 'hidden' }}
                  >
                    {ListItem}
                  </FixedSizeList>
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
          {isLoading && itemCount === 0 && <div className="px-2">{loader}</div>}
          {itemCount === 0 && !isLoading && (
            <div role="menuitem">
              <TextStyle variant="sm-regular" className="px-6 pb-2 text-gray-8">
                {formatMessage(messages.noResultsFound)}
              </TextStyle>
            </div>
          )}
        </div>
      </div>
      {itemCount > 0 && (
        <div
          className={twMerge(
            'grid w-full grid-cols-[1fr_auto] justify-between gap-2 rounded-b-lg bg-gray-1 p-2',
            buttonsWrapperClassName
          )}
        >
          <Button
            variation="secondaryEmphasized"
            onClick={() => onDoneClick(currentSelectedOptions)}
            size="small"
          >
            {formatMessage(messages.done)}
          </Button>
          <Button
            size="small"
            variation="secondaryLite"
            onClick={handleClearAllClick}
            disabled={currentSelectedOptions.length === 0}
          >
            {formatMessage(messages.clearAll)}
          </Button>
        </div>
      )}
    </section>
  );
}
